/**
 * Every string the category surface renders.
 *
 * The copy addresses the operator, never the reviewer. Three kinds of sentence
 * are barred and none of them is here: **design rationale** («the vocabulary is
 * read from a setting»), **technical vocabulary** (a table name, a hostname, a
 * relation kind), and **restatements of visible mechanics** («deletion is
 * confirmed before it runs», printed above the dialog that is about to confirm
 * it). What survives states what the surface drives and what the operator cannot
 * see: that a name written here is what the visitor reads, and that a deletion
 * leaves the titles in the catalogue.
 */
export const copy = {
  crumbs: {
    root: 'لوحة التشغيل',
    current: 'التصنيفات',
  },

  head: {
    title: 'التصنيفات',
    lede: 'شجرة التصنيفات التي يُصنَّف بها الفهرس، وهي نفسها ما يتصفّحه الزائر: العمود الجانبي في الموقع العامّ، وصفحة كلّ تصنيف، والرفوف التي مصدرها تصنيف. الشجرة تقبل أيّ عمق، ولكلّ تصنيف أن يحمل تصنيفاتٍ تحته.',
  },

  section: {
    heading: 'شجرة التصنيفات',
    add: 'أضف تصنيفاً جذرياً',
    context:
      'التصنيف تسميةٌ للعناوين لا مكانٌ تُخزَّن فيه: حذفه أو نقله يغيّر ما يجده الزائر تحته، ولا يمسّ العناوين نفسها. وكلّ اسم يُكتب هنا يقرأه الزائر كما هو — في العمود الجانبي، وفي ترويسة صفحة التصنيف، وفي فتات الطريق فوقها.',
    treeLabel: 'شجرة التصنيفات',
  },

  /** The three actions every node carries, behind a deliberate open. */
  menu: {
    label: (name: string) => `أفعال تصنيف «${name}»`,
    edit: 'حرّر التصنيف',
    addChild: 'أضف تصنيفاً فرعياً',
    delete: 'احذف التصنيف',
  },

  dialog: {
    /**
     * The root-add title names the act the trigger named. The pack's demo
     * script composes «أضف» + «التصنيف» here, which reads as «add *the*
     * category» — a definite article on a record that does not exist yet, and
     * the one string on this surface where the mockup layer's concatenation
     * produces worse Arabic than its own button label.
     */
    titleCreateRoot: 'أضف تصنيفاً جذرياً',
    titleCreateChild: 'أضف تصنيفاً فرعياً',
    titleEdit: 'حرّر التصنيف',
    context: 'شجرة التصنيفات',
    nameLabel: 'اسم التصنيف',
    parentLabel: 'التصنيف الأب',
    parentRoot: '— بلا أب (تصنيف جذريّ) —',
    parentHint:
      'موضع التصنيف في الشجرة. اتركه على «بلا أب» ليقف في أعلى مستوى، أو اختر تصنيفاً ليقع تحته. التصنيفات معروضة بمسارها الكامل لأنّ الاسم وحده قد يتكرّر تحت أكثر من أب.',
    save: 'احفظ',
  },

  validation: {
    nameRequired: 'اكتب اسم التصنيف.',
    nameTooLong: 'الاسم أطول ممّا يقبله الحقل. اختصره.',
  },

  /**
   * The confirmation claims only what the catalogue supports.
   *
   * It says the titles stay, because a category is a label rather than a
   * container. It says **nothing** about what happens to the categories under a
   * deleted parent: the deletion behaviour the platform configures refuses a
   * category that still holds one, and the refusal names what is in the way when
   * it arrives — so a sentence here predicting a cascade or a re-parent would be
   * predicting the wrong one half the time.
   */
  confirm: {
    heading: (name: string) => `حذف تصنيف «${name}» نهائياً؟`,
    body: 'يختفي التصنيف من العمود الجانبي ومن صفحته فوراً، ويتوقّف كلّ رفٍّ مصدره هذا التصنيف عن عرض شيء. العناوين التي كانت تحته تبقى في الفهرس كما هي.',
    commit: 'احذف التصنيف',
  },

  /** One polite region for the surface. A figure never stands alone in it. */
  announce: {
    created: (name: string) => `أُضيف التصنيف «${name}».`,
    updated: (name: string) => `حُفظ التصنيف «${name}».`,
    deleted: (name: string) => `حُذف التصنيف «${name}».`,
  },

  states: {
    loading: 'جارٍ تحميل شجرة التصنيفات',
    empty: 'لا تصنيفات بعد. ابدأ بتصنيف جذريّ، ثمّ علّق تحته ما يتفرّع عنه.',
  },
} as const;
